/**
 * The amusement park: a day pass at the gate, then the rides (the big wheel, the roller coaster, the carousel, the
 * bumper cars) and the games (the claw machine, the high striker), cotton candy, and a flower stall. What you win and
 * what you buy can go home with you (a toy on the bed, flowers in a vase) or to someone: a contact (closer for it) or
 * another player (it arrives at their home, from you). The park is for being there together: the rides run on the
 * real clock, so players who board together ride together.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const PARKS: Record<string, string> = {
  lagos: 'Carnival Island, Lekki', us: 'Starfall Park, New Jersey', france: 'Parc des Lumières, Paris', china: 'Golden Lantern Park, Shanghai',
  japan: 'Hoshizora Land, Tokyo', italy: 'Luna Park Aurora, Rome', uae: 'Desert Star Park, Dubai',
};
export const fairFor = (region: string): string => PARKS[region] ?? PARKS.lagos;
export const fairPass = (s: LifeState) => localPrice(s, 12_000, 500);

export type FairRide = 'wheel' | 'coaster' | 'carousel' | 'bumper';
export type FairGame = 'claw' | 'striker';
export interface Prize { kind: string; label: string; colour: number }
/** the toys in the claw machine (and the striker's top prize) */
export const PLUSH: Prize[] = [
  { kind: 'bear', label: 'a teddy bear', colour: 0xb07a4a }, { kind: 'bear', label: 'a pink teddy', colour: 0xf2a0c0 },
  { kind: 'bunny', label: 'a white bunny', colour: 0xf2f0ea }, { kind: 'frog', label: 'a green frog', colour: 0x6ac04a },
  { kind: 'bear', label: 'a blue bear', colour: 0x6aa8e8 }, { kind: 'bunny', label: 'a lilac bunny', colour: 0xc8a8e8 },
  { kind: 'duck', label: 'a yellow duck', colour: 0xf2d02a }, { kind: 'frog', label: 'a red frog', colour: 0xe0503a },
];
export const FLOWERS = [
  { id: 'roses', label: 'A dozen red roses', short: 'roses', price: 15_000, colour: 0xc8102e },
  { id: 'sunflowers', label: 'Sunflowers', short: 'sunflowers', price: 9_000, colour: 0xf2c12e },
  { id: 'lilies', label: 'White lilies', short: 'lilies', price: 11_000, colour: 0xf4f2ea },
  { id: 'tulips', label: 'Mixed tulips', short: 'tulips', price: 10_000, colour: 0xe85a9a },
] as const;
export type FlowerId = (typeof FLOWERS)[number]['id'];
export const flowerPrice = (s: LifeState, id: FlowerId) => localPrice(s, FLOWERS.find((f) => f.id === id)!.price, 500);
export const clawPrice = (s: LifeState) => localPrice(s, 1_000, 100);
export const strikerPrice = (s: LifeState) => localPrice(s, 1_000, 100);
export const candyPrice = (s: LifeState) => localPrice(s, 1_500, 100);

interface FairState { day?: number; rides?: Record<string, number>; prizes?: Prize[]; flowersAt?: { kind: string; from: string; t: number }[] }
const of = (s: LifeState): FairState => ((s as LifeState & { fair?: FairState }).fair ??= {});
export const fairOf = of;
export const hasFairPass = (s: LifeState) => of(s).day === Math.floor(s.minutes / 1440);
/** toys you've won (or been given), kept at home */
export const prizesOf = (s: LifeState) => of(s).prizes ?? [];
/** flowers in the vase at home (the freshest few; flowers last a week) */
export const flowersAtHome = (s: LifeState) => (of(s).flowersAt ?? []).filter((f) => s.minutes - f.t < 7 * 1440);

export function buyFairPass(s: LifeState): { ok: boolean; line: string } {
  if (hasFairPass(s)) return { ok: true, line: 'Your wristband is good all day.' };
  if (!spend(s, fairPass(s), 'Amusement park day pass')) return { ok: false, line: "You can't afford the day pass." };
  const f = of(s); f.day = Math.floor(s.minutes / 1440);
  if (!f.rides) s.history.push({ t: s.minutes, text: 'A day at the amusement park.' });
  return { ok: true, line: 'A neon wristband snaps on. The music, the screams from the coaster, the smell of sugar.' };
}

const RIDE_LINES: Record<FairRide, [string, string]> = {
  wheel: ['Round and up over the whole park, the lights coming on below. At the top, everything goes quiet.', 'Another turn on the wheel. The city looks small from up here.'],
  coaster: ['The click-click-click of the lift, the pause at the top, and then your stomach stays up there. You scream; everyone screams.', 'Again! The drop is just as bad. Just as good.'],
  carousel: ['Round and round on a painted horse, the organ music playing, up and down. You feel about eight.', 'Another go on the horses. You pick a different one.'],
  bumper: ['Bang! Somebody hits you from behind; you get them back at the corner. You laugh till the power cuts.', 'More bumper cars. You have a score to settle.'],
};
export function rodeRide(s: LifeState, ride: FairRide): { line: string; events: LifeEvent[] } {
  const f = of(s), n = ((f.rides ??= {})[ride] = (f.rides[ride] ?? 0) + 1);
  s.needs.fun = Math.min(100, s.needs.fun + (n === 1 ? 14 : 7));
  s.needs.social = Math.min(100, s.needs.social + 2);
  if (ride === 'coaster') s.needs.energy = Math.max(0, s.needs.energy - 3);
  return { line: RIDE_LINES[ride][n === 1 ? 0 : 1], events: advance(s, ride === 'wheel' ? 15 : 8, { busy: true }) };
}

/** a go on a game (paid up front) */
export function payGame(s: LifeState, game: FairGame): boolean { return spend(s, game === 'claw' ? clawPrice(s) : strikerPrice(s), game === 'claw' ? 'Claw machine' : 'High striker'); }
/** the claw closed on something: `aim` 0..1 (how centred), it holds or it drops (the claws are weak, like real ones) */
export function clawGrab(aim: number, roll: number): boolean { return roll < 0.12 + aim * 0.55; }
export function wonPrize(s: LifeState, p: Prize): { line: string } {
  (of(s).prizes ??= []).push(p);
  s.needs.fun = Math.min(100, s.needs.fun + 10);
  return { line: `You won ${p.label}!` };
}
/** give a prize you're holding to someone (a contact by name: closer; or a player: the game sends it) */
export function givePrize(s: LifeState, p: Prize, to: string, player: boolean): { line: string } {
  const list = of(s).prizes ?? [], i = list.indexOf(p);
  if (i >= 0) list.splice(i, 1);
  if (!player) { const c = s.contacts.find((x) => x.name === to); if (c) c.closeness = Math.min(100, c.closeness + 6); }
  s.needs.social = Math.min(100, s.needs.social + 4);
  return { line: `You give ${to.split(' ')[0]} ${p.label}.` };
}

/** flowers: kept (they go home in a vase), or for someone (a contact, or a player: it arrives at their home) */
export function buyFlowers(s: LifeState, id: FlowerId, to: { name: string; player: boolean } | null): { ok: boolean; line: string; events: LifeEvent[] } {
  const f = FLOWERS.find((x) => x.id === id)!;
  if (!spend(s, flowerPrice(s, id), `${f.label}${to ? ` for ${to.name}` : ''}`)) return { ok: false, line: "You can't afford them.", events: [] };
  if (!to) { (of(s).flowersAt ??= []).push({ kind: id, from: 'you', t: s.minutes }); s.needs.fun = Math.min(100, s.needs.fun + 3); return { ok: true, line: `${f.label}, wrapped in brown paper. They'll be in a vase at home when you get back.`, events: [] }; }
  if (!to.player) {
    const c = s.contacts.find((x) => x.name === to.name);
    if (c) c.closeness = Math.min(100, c.closeness + (/Partner|Girlfriend|Boyfriend|Wife|Husband/.test(c.relation) ? 10 : 6));
    s.history.push({ t: s.minutes, text: `Sent ${to.name} ${f.short}.` });
  }
  s.needs.social = Math.min(100, s.needs.social + 4);
  return { ok: true, line: `${f.label}, on their way to ${to.name.split(' ')[0]}, with a card from you.`, events: [] };
}
/** somebody sent you flowers (a player, through the inbox): in a vase at home, and a message */
export function receivedFlowers(s: LifeState, from: string, id: string, note: string) {
  const f = FLOWERS.find((x) => x.id === id) ?? FLOWERS[0];
  (of(s).flowersAt ??= []).push({ kind: f.id, from, t: s.minutes });
  s.needs.social = Math.min(100, s.needs.social + 8); s.needs.fun = Math.min(100, s.needs.fun + 5);
  s.messages.unshift({ t: s.minutes, from, text: `💐 ${f.label} for you${note ? `: "${note}".` : '.'} They're in a vase at home.`, read: false });
}
export function receivedPrize(s: LifeState, from: string, p: Prize, note: string) {
  (of(s).prizes ??= []).push(p);
  s.needs.social = Math.min(100, s.needs.social + 6);
  s.messages.unshift({ t: s.minutes, from, text: `🧸 ${from.split(' ')[0]} won you ${p.label} at the fair${note ? `: "${note}"` : '.'} It's at home.`, read: false });
}
export function buyCandy(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, candyPrice(s), 'Cotton candy')) return { ok: false, line: "You can't afford it.", events: [] };
  s.needs.hunger = Math.min(100, s.needs.hunger + 6); s.needs.fun = Math.min(100, s.needs.fun + 3);
  return { ok: true, line: 'A pink cloud on a stick. It sticks to your face; you don\'t care.', events: advance(s, 3, { busy: true }) };
}
